export default function SafetyHarborHelpfulResourcesSection() {
  return (
    <section className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Resources</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Helpful Resources for Safety Harbor Travelers
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Local services, offices, and travel information for residents of Safety Harbor and northern Pinellas County preparing for international travel.
          </p>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden mb-8">
          <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0">
              <i className="ri-file-text-line text-white text-lg"></i>
            </div>
            <div>
              <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Vital Records</p>
              <p className="text-white font-bold text-base leading-tight mt-0.5">Birth, Death, Marriage &amp; Divorce Records — Pinellas County</p>
            </div>
          </div>
          <div className="px-6 py-6">
            <p className="text-gray-500 text-sm leading-relaxed mb-5">
              If you need a certified copy of your birth certificate for a passport application, a marriage certificate for a name change, or a divorce decree to support your passport correction, here are the official sources for vital records in Pinellas County. Most records can be ordered online for delivery within 5–10 business days.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
              {[
                { icon: "ri-user-smile-line", label: "Birth Certificate", desc: "Request certified birth certificates from the Florida Department of Health in Pinellas County. Online, mail, or in-person options available.", url: "https://pinellas.floridahealth.gov/" },
                { icon: "ri-file-list-2-line", label: "Death Certificate", desc: "Obtain certified death certificates through the Florida Department of Health in Pinellas County. Required for estate and legal matters.", url: "https://pinellas.floridahealth.gov/" },
                { icon: "ri-hearts-line", label: "Marriage Certificate", desc: "Request marriage license records from the Pinellas County Clerk of Courts. Certified copies available for name changes and legal purposes.", url: "https://www.pinellasclerk.org/" },
                { icon: "ri-file-copy-line", label: "Divorce Decree", desc: "Obtain certified divorce records through the Pinellas County Clerk of Courts. Required for name change passport corrections and legal documentation.", url: "https://www.pinellasclerk.org/" },
              ].map((record, i) => (
                <a
                  key={i}
                  href={record.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-3.5 bg-[#F7F8FA] border border-gray-200 rounded-lg p-4 hover:border-[#F6C344] transition-all group"
                >
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#EEF2F8] flex-shrink-0 mt-0.5">
                    <i className={`${record.icon} text-[#1A365D] text-base group-hover:text-[#F6C344] transition-colors`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-[#1A365D] text-xs leading-snug mb-1">{record.label}</p>
                    <p className="text-gray-500 text-xs leading-relaxed mb-1.5">{record.desc}</p>
                    <span className="inline-flex items-center gap-1 text-[#1A365D] font-semibold text-xs group-hover:text-[#F6C344] transition-colors">
                      <i className="ri-external-link-line text-xs"></i>Get Records
                    </span>
                  </div>
                </a>
              ))}
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
              <p className="text-gray-600 text-xs leading-relaxed">
                <i className="ri-information-line text-amber-500 mr-1.5"></i>
                <strong>Don&apos;t see your county?</strong> If the record you need was issued outside Pinellas County, visit <a href="https://www.vitalchek.com/" target="_blank" rel="noopener noreferrer" className="font-bold text-[#1A365D] underline underline-offset-2 hover:text-[#F6C344] transition-colors">VitalChek.com</a> — the official online service for ordering vital records nationwide across all U.S. states and counties.
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Passport Photos */}
          <div className="bg-white rounded-lg border border-gray-200 p-6 hover:border-[#F6C344] transition-all duration-200">
            <div className="w-11 h-11 flex items-center justify-center rounded-full bg-[#EEF2F8] mb-4">
              <i className="ri-camera-line text-[#1A365D] text-xl"></i>
            </div>
            <h3 className="font-bold text-[#1A365D] text-sm mb-3">Passport Photo Locations Near Safety Harbor</h3>
            <ul className="space-y-2.5">
              {[
                "CVS Pharmacy — 1000 Main St, Safety Harbor, FL",
                "Walgreens — 2900 State Rd 580, Safety Harbor, FL",
                "UPS Store — 34980 US Hwy 19 N, Palm Harbor, FL",
                "FedEx Office — 2511 Gulf to Bay Blvd, Clearwater, FL",
                "Walgreens — 2001 Gulf to Bay Blvd, Clearwater, FL",
              ].map((loc, i) => (
                <li key={i} className="flex items-start gap-2 text-gray-500 text-xs">
                  <i className="ri-map-pin-line text-[#F6C344] text-xs mt-0.5 flex-shrink-0"></i>
                  <span>{loc}</span>
                </li>
              ))}
            </ul>
            <p className="text-gray-400 text-xs mt-3 italic">
              Call ahead to confirm passport photo availability and current wait times.
            </p>
          </div>

          {/* DMV / ID Offices */}
          <div className="bg-white rounded-lg border border-gray-200 p-6 hover:border-[#F6C344] transition-all duration-200">
            <div className="w-11 h-11 flex items-center justify-center rounded-full bg-[#EEF2F8] mb-4">
              <i className="ri-car-line text-[#1A365D] text-xl"></i>
            </div>
            <h3 className="font-bold text-[#1A365D] text-sm mb-3">DMV & ID Services — Pinellas County</h3>
            <ul className="space-y-2.5">
              {[
                "Pinellas County Tax Collector — 29399 US Hwy 19 N, Clearwater, FL",
                "Pinellas County Tax Collector — 1800 66th St N, St. Petersburg, FL",
                "Pinellas County Tax Collector — 13025 Starkey Rd, Largo, FL",
                "Florida Highway Safety & Motor Vehicles — Online services at fhsmv.gov",
              ].map((loc, i) => (
                <li key={i} className="flex items-start gap-2 text-gray-500 text-xs">
                  <i className="ri-map-pin-line text-[#F6C344] text-xs mt-0.5 flex-shrink-0"></i>
                  <span>{loc}</span>
                </li>
              ))}
            </ul>
            <p className="text-gray-400 text-xs mt-3 italic">
              A valid driver's license or state ID is required for all passport applications.
            </p>
          </div>

          {/* Airports */}
          <div className="bg-white rounded-lg border border-gray-200 p-6 hover:border-[#F6C344] transition-all duration-200">
            <div className="w-11 h-11 flex items-center justify-center rounded-full bg-[#EEF2F8] mb-4">
              <i className="ri-plane-line text-[#1A365D] text-xl"></i>
            </div>
            <h3 className="font-bold text-[#1A365D] text-sm mb-3">Nearby Airports for Safety Harbor Travelers</h3>
            <ul className="space-y-2.5">
              {[
                "Tampa International Airport (TPA) — 25 min south via US-19 / Veterans Expy",
                "St. Pete-Clearwater International (PIE) — 20 min south via US-19",
                "Orlando International Airport (MCO) — 1 hr 45 min east via I-4",
                "Sarasota-Bradenton International (SRQ) — 1 hr south via I-275",
              ].map((loc, i) => (
                <li key={i} className="flex items-start gap-2 text-gray-500 text-xs">
                  <i className="ri-map-pin-line text-[#F6C344] text-xs mt-0.5 flex-shrink-0"></i>
                  <span>{loc}</span>
                </li>
              ))}
            </ul>
            <p className="text-gray-400 text-xs mt-3 italic">
              TPA offers the most international flights and is the preferred airport for most Safety Harbor travelers.
            </p>
          </div>
        </div>

        {/* Additional resources */}
        <div className="mt-8 bg-[#1A365D] rounded-lg p-6">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#F6C344] flex-shrink-0">
              <i className="ri-links-line text-[#1A365D] text-xl"></i>
            </div>
            <div className="flex-1">
              <p className="text-white font-bold text-sm mb-2">Additional Government Resources</p>
              <div className="flex flex-wrap gap-3">
                {[
                  { name: "travel.state.gov", url: "https://travel.state.gov/content/travel/en/passports.html" },
                  { name: "TSA PreCheck", url: "https://www.tsa.gov/precheck" },
                  { name: "Global Entry", url: "https://www.cbp.gov/travel/trusted-traveler-programs/global-entry" },
                  { name: "CDC Travel Health", url: "https://wwwnc.cdc.gov/travel" },
                ].map((link) => (
                  <a
                    key={link.url}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-[#F6C344] hover:text-white font-medium transition-colors bg-white/5 hover:bg-white/10 rounded-full px-3 py-1.5"
                  >
                    {link.name} <i className="ri-external-link-line text-[10px]"></i>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}